/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getMyAvailability, updateMyAvailability } from '@/lib/telemedicine';
import { DAYS } from './doctorConstants';

/**
 * AvailabilityDialog — weekly availability windows + slot/buffer config.
 * Extracted from DoctorCommandCenter.jsx (Code Quality refactor).
 */
export const AvailabilityDialog = ({ open, onOpenChange, onSaved }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [windows, setWindows] = useState([]);
  const [slotMin, setSlotMin] = useState(30);
  const [bufferMin, setBufferMin] = useState(10);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    getMyAvailability()
      .then((res) => {
        if (cancelled) return;
        setWindows(res.weekly_windows || []);
        setSlotMin(res.slot_minutes || 30);
        setBufferMin(res.buffer_minutes || 10);
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') console.debug('[Availability] load failed', err);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const toggleDay = (dayIdx) => {
    const exists = windows.find((w) => w.day === dayIdx);
    if (exists) {
      setWindows(windows.filter((w) => w.day !== dayIdx));
    } else {
      setWindows([...windows, { day: dayIdx, start: '09:00', end: '18:00' }].sort((a, b) => a.day - b.day));
    }
  };

  const updateWindow = (dayIdx, field, value) => {
    setWindows(windows.map((w) => w.day === dayIdx ? { ...w, [field]: value } : w));
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateMyAvailability({
        weekly_windows: windows,
        slot_minutes: Number(slotMin),
        buffer_minutes: Number(bufferMin),
      });
      toast.success(t('availabilitySaved'));
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('genericError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="availability-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Settings2 className="h-5 w-5 text-primary" /> {t('availabilityTitle')}</DialogTitle>
          <DialogDescription>{t('availabilityDesc')}</DialogDescription>
        </DialogHeader>
        {loading ? <Skeleton className="h-40" /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('availabilitySlotMin')}</Label>
                <Input type="number" min={15} max={120} step={5} value={slotMin} onChange={(e) => setSlotMin(e.target.value)} data-testid="availability-slot-min" />
              </div>
              <div>
                <Label>{t('availabilityBufferMin')}</Label>
                <Input type="number" min={0} max={30} step={5} value={bufferMin} onChange={(e) => setBufferMin(e.target.value)} data-testid="availability-buffer-min" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{t('availabilityDays')}</Label>
              {DAYS.map((label, idx) => {
                const win = windows.find((w) => w.day === idx);
                return (
                  <div key={label} className={cn('flex items-center gap-3 rounded-xl border p-2.5 transition-colors', win ? 'border-primary/40 bg-primary/5' : 'border-border/60')}>
                    <button
                      onClick={() => toggleDay(idx)}
                      className={cn('h-9 w-11 rounded-lg font-bold text-xs uppercase transition-colors flex-shrink-0', win ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}
                      data-testid={`availability-day-toggle-${idx}`}
                    >
                      {label}
                    </button>
                    {win ? (
                      <>
                        <Input type="time" value={win.start} onChange={(e) => updateWindow(idx, 'start', e.target.value)} className="h-9 max-w-[110px]" data-testid={`availability-start-${idx}`} />
                        <span className="text-xs text-muted-foreground">→</span>
                        <Input type="time" value={win.end} onChange={(e) => updateWindow(idx, 'end', e.target.value)} className="h-9 max-w-[110px]" data-testid={`availability-end-${idx}`} />
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">{t('availabilityClosed')}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('cancel')}</Button>
          <Button onClick={save} disabled={saving} data-testid="availability-save-btn">{saving ? t('saving') : t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
